import i18next from "i18next"
import JSZip from "jszip"

import { filterAllowedFiles, TENDER_ACCEPT_FILTER } from "../tender-upload-constants"

const IGNORED_FILES = new Set([".ds_store", "thumbs.db", "desktop.ini"])

/** Build an extension → MIME lookup from the existing accept filter. */
const EXT_TO_MIME: Record<string, string> = {}
for (const [mime, exts] of Object.entries(TENDER_ACCEPT_FILTER)) {
  for (const ext of exts) EXT_TO_MIME[ext] = mime
}

function guessMimeType(filename: string): string {
  const ext = filename.slice(filename.lastIndexOf(".")).toLowerCase()
  return EXT_TO_MIME[ext] ?? ""
}

const MAX_ZIP_SIZE = 200 * 1024 * 1024 // 200 MB
// Per-file cap mirrors the SAS-upload contract on the backend: anything
// bigger than this is almost certainly a video or backup, not a procurement
// document, and lets the modal block obvious mistakes early.
export const MAX_FILE_SIZE = 1024 * 1024 * 1024 // 1 GB

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / 1024 / 1024)} MB`
  return `${Math.round(bytes / 1024)} KB`
}

export { formatFileSize }

function isIgnored(path: string): boolean {
  const name = path.split("/").pop()?.toLowerCase() ?? ""
  if (IGNORED_FILES.has(name)) return true
  if (name.startsWith(".")) return true
  if (path.includes("__MACOSX/")) return true
  return false
}

/**
 * Must be called **synchronously** inside the drop handler —
 * the browser clears dataTransfer after the event.
 */
export function captureDropEntries(dataTransfer: DataTransfer): FileSystemEntry[] {
  const entries: FileSystemEntry[] = []
  for (let i = 0; i < dataTransfer.items.length; i++) {
    const entry = dataTransfer.items[i].webkitGetAsEntry()
    if (entry) entries.push(entry)
  }
  return entries
}

function readEntriesPromise(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => reader.readEntries(resolve, reject))
}

function fileFromEntry(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject))
}

async function traverseEntry(entry: FileSystemEntry, collected: File[], warnings: string[]) {
  if (isIgnored(entry.fullPath)) return

  if (entry.isFile) {
    try {
      const file = await fileFromEntry(entry as FileSystemFileEntry)
      if (file.size === 0) return
      collected.push(file)
    } catch {
      warnings.push(i18next.t("tender.upload.warnings.couldNotRead", { path: entry.fullPath }))
    }
    return
  }

  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader()
    try {
      let batch: FileSystemEntry[]
      do {
        batch = await readEntriesPromise(reader)
        for (const child of batch) {
          await traverseEntry(child, collected, warnings)
        }
      } while (batch.length > 0)
    } catch {
      warnings.push(
        i18next.t("tender.upload.warnings.couldNotReadFolder", { path: entry.fullPath })
      )
    }
  }
}

async function extractZip(file: File, warnings: string[]): Promise<File[]> {
  if (file.size > MAX_ZIP_SIZE) {
    warnings.push(i18next.t("tender.upload.warnings.zipTooLarge", { name: file.name }))
    return []
  }

  try {
    const zip = await JSZip.loadAsync(await file.arrayBuffer())
    const extracted: File[] = []

    for (const [path, entry] of Object.entries(zip.files)) {
      if (entry.dir) continue
      if (path.includes("__MACOSX/")) continue
      if (isIgnored(path)) continue

      const blob = await entry.async("blob")
      if (blob.size === 0) continue

      const name = path.split("/").pop() ?? path
      // Carry the archive entry's own mtime through — without it, File
      // defaults lastModified to Date.now() at construction, so two same-name
      // same-size entries from different zip folders (paths are flattened to
      // basenames) built in the same millisecond collide on tenderFileKey and
      // one silently vanishes from the batch.
      extracted.push(
        new File([blob], name, { type: guessMimeType(name), lastModified: entry.date.getTime() })
      )
    }

    return extracted
  } catch {
    warnings.push(i18next.t("tender.upload.warnings.zipExtractFailed", { name: file.name }))
    return []
  }
}

async function processCollectedFiles(raw: File[], warnings: string[]): Promise<File[]> {
  const expanded: File[] = []

  for (const file of raw) {
    if (file.name.toLowerCase().endsWith(".zip")) {
      const inner = await extractZip(file, warnings)
      expanded.push(...inner)
    } else {
      expanded.push(file)
    }
  }

  // Per-file size cap (mirrors the zipTooLarge pattern just above): warn
  // once per oversized file and drop it from the batch.
  const sizedAllowed: File[] = []
  for (const f of expanded) {
    if (f.size > MAX_FILE_SIZE) {
      warnings.push(
        i18next.t("tender.upload.warnings.fileTooLarge", {
          name: f.name,
          max: formatFileSize(MAX_FILE_SIZE),
        })
      )
      continue
    }
    sizedAllowed.push(f)
  }

  // Filter allowed extensions
  const { allowed, rejected } = filterAllowedFiles(sizedAllowed)
  if (rejected.length > 0) {
    warnings.push(
      i18next.t("tender.upload.warnings.unsupportedTypes", { list: rejected.join(", ") })
    )
  }

  // Duplicate name warning
  const seen = new Map<string, number>()
  for (const f of allowed) {
    seen.set(f.name, (seen.get(f.name) ?? 0) + 1)
  }
  const dupes = [...seen.entries()].filter(([, c]) => c > 1).map(([n]) => n)
  if (dupes.length > 0) {
    warnings.push(i18next.t("tender.upload.warnings.duplicateNames", { list: dupes.join(", ") }))
  }

  return allowed
}

/**
 * Resolves FileSystemEntry[] (from drag-and-drop) into a flat file list.
 * Traverses directories recursively and extracts ZIP files.
 */
export async function resolveEntriesToFiles(
  entries: FileSystemEntry[]
): Promise<{ files: File[]; warnings: string[] }> {
  const collected: File[] = []
  const warnings: string[] = []

  for (const entry of entries) {
    await traverseEntry(entry, collected, warnings)
  }

  const files = await processCollectedFiles(collected, warnings)
  return { files, warnings }
}

/**
 * Processes a flat FileList (from folder input or file input) —
 * extracts ZIPs and filters allowed extensions.
 */
export async function resolveFileList(
  fileList: FileList
): Promise<{ files: File[]; warnings: string[] }> {
  const warnings: string[] = []
  const raw = Array.from(fileList).filter((f) => {
    if (f.size === 0) return false
    const path = (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name
    return !isIgnored(path)
  })

  const files = await processCollectedFiles(raw, warnings)
  return { files, warnings }
}
